import { Button, Tooltip, useAlert } from "@prismicio/editor-ui"
import {
  Document,
  type TextContent,
} from "@prismicio/types-internal/lib/content"
import type {
  StaticCustomType,
  Text,
} from "@prismicio/types-internal/lib/customtypes"
import { useMemo, useState } from "react"

import { MetadataBadge } from "../core/SeoMetadata"
import {
  generateSEOMetadata,
  getPageTextContent,
  isPageContentRelevantForAISeo,
  type MetadataType,
  metaDescriptionProperty,
  metaTitleProperty,
} from "../core/service"
import { useEditorConfig } from "../EditorConfig"
import { KeyTextField } from "./KeyTextField"

interface MetadataTextFieldProps {
  content: TextContent | undefined
  documentId: string
  documentContent: Document
  documentCustomType: StaticCustomType
  documentLocale: string
  field: Text
  id: string
  metadataFieldType: MetadataType
  onContentChange: (content: TextContent | undefined) => void
  readOnly: boolean
}

export function MetadataTextField(props: MetadataTextFieldProps) {
  const {
    content,
    metadataFieldType,
    documentId,
    documentContent,
    documentCustomType,
    documentLocale,
    ...staticWidgetProps
  } = props

  const { showAlert } = useAlert()
  const [loading, setLoading] = useState(false)
  const config = useEditorConfig()

  const pageTextContent = useMemo(
    () => getPageTextContent(documentContent, documentCustomType),
    [documentContent, documentCustomType],
  )

  const isContentSeoRelevant = isPageContentRelevantForAISeo(pageTextContent)

  async function onClickGenerate() {
    if (!config.aiSeoMetadataEnabled) return

    const {
      awsBedrockRegion,
      awsBedrockAccessKeyId,
      awsBedrockSecretAccessKey,
      trackMetadataGeneration,
    } = config

    setLoading(true)

    try {
      const response = await generateSEOMetadata({
        pageTextContent,
        contentLocale: documentLocale,
        config: {
          region: awsBedrockRegion,
          accessKeyId: awsBedrockAccessKeyId,
          secretAccessKey: awsBedrockSecretAccessKey,
        },
      })

      props.onContentChange({
        __TYPE__: "FieldContent",
        type: "Text",
        value: response[metadataFieldType],
      })

      trackMetadataGeneration({
        documentId,
        locale: documentLocale,
        suggestion: response[metadataFieldType],
        type: metadataFieldType,
      })
    } catch (error) {
      showAlert({
        id: metadataFieldType,
        title: `Error`,
        subtitle: `Failed to generate SEO metadata for ${metadataFieldLabel[metadataFieldType]}`,
      })
    }

    setLoading(false)
  }

  return (
    <KeyTextField
      {...staticWidgetProps}
      content={content}
      placeholder={`${metadataFieldLabel[metadataFieldType]}..`}
      endAdornment={
        <>
          <MetadataBadge
            type={metadataFieldType}
            value={content?.value || ""}
          />

          <Tooltip
            content={
              isContentSeoRelevant
                ? "Generate SEO metadata from your page content."
                : "Your page content is too short to generate SEO metadata."
            }
          >
            <Button
              disabled={!isContentSeoRelevant}
              onClick={onClickGenerate}
              size="small"
              color="grey"
              startIcon="autoFixHigh"
              loading={loading}
              sx={{ alignSelf: "center", marginInline: 8 }}
            >
              Generate
            </Button>
          </Tooltip>
        </>
      }
    />
  )
}

const metadataFieldLabel: Record<MetadataType, string> = {
  [metaTitleProperty]: "Meta title",
  [metaDescriptionProperty]: "Meta description",
}
